/* ═══════════════════════════════════════════════════════════════
   CategoryEntryCard — one category entry: folder icon, name, count,
   right-click + "more" menu. Split out of ModuleBrowser.tsx mechanically
   (no behavior change) — every prop and interaction is exactly what it
   was before, just moved to its own file.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Folder, MoreVertical, Pencil, FolderInput, Trash2, Plus } from 'lucide-react';
import { Icon } from './ui/Icon';
import { rem } from '../lib/rem';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from './ui/context-menu';
import type { ModuleCategory } from '../../shared/module-categories';

export function CategoryEntryCard({
  cat,
  count,
  canCreate,
  canEdit,
  canDelete,
  onOpen,
  onNewSub,
  onRename,
  onMove,
  onDelete,
}: {
  cat: ModuleCategory;
  count: number;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onNewSub: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const hasMenu = canCreate || canEdit || canDelete;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      onContextMenu={(e) => {
        if (!hasMenu) return;
        e.preventDefault();
        setMenuOpen(true);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: `${rem(8)} var(--space-3)`,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--bd)',
        background: 'var(--sf)',
        cursor: 'pointer',
        minHeight: rem(34),
      }}
    >
      <Icon
        icon={Folder}
        size="sm"
        aria-hidden="true"
        style={{ color: 'var(--ac)', flexShrink: 0 }}
      />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--tx)',
          fontWeight: 600,
        }}
      >
        {cat.name}
      </span>
      <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)', flexShrink: 0 }}>
        {count}
      </span>
      {hasMenu && (
        <ContextMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ContextMenuTrigger asChild>
            <button
              type="button"
              aria-label={`More actions for ${cat.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: rem(2),
                display: 'flex',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--tm)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <Icon icon={MoreVertical} size="sm" aria-hidden="true" />
            </button>
          </ContextMenuTrigger>
          <ContextMenuContent className="dsg-menu" onClick={(e) => e.stopPropagation()}>
            {canCreate && (
              <ContextMenuItem className="dsg-menu-item" onSelect={onNewSub}>
                <Icon icon={Plus} size="sm" aria-hidden="true" /> New sub-category
              </ContextMenuItem>
            )}
            {canEdit && (
              <ContextMenuItem className="dsg-menu-item" onSelect={onRename}>
                <Icon icon={Pencil} size="sm" aria-hidden="true" /> Rename
              </ContextMenuItem>
            )}
            {canEdit && (
              <ContextMenuItem className="dsg-menu-item" onSelect={onMove}>
                <Icon icon={FolderInput} size="sm" aria-hidden="true" /> Move to…
              </ContextMenuItem>
            )}
            {canDelete && (
              <>
                <ContextMenuSeparator className="dsg-menu-sep" />
                <ContextMenuItem className="dsg-menu-item" onSelect={onDelete}>
                  <Icon icon={Trash2} size="sm" aria-hidden="true" /> Delete
                </ContextMenuItem>
              </>
            )}
          </ContextMenuContent>
        </ContextMenu>
      )}
    </div>
  );
}

export default CategoryEntryCard;
